import {
  type ComponentType,
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useMemo,
  useReducer,
} from 'react';

export enum ModalView {
  AccountPersonalisation = 'AccountPersonalisation',
  LoginForm = 'LoginForm',
  LoginVerificationForm = 'LoginVerificationForm',
  SignupForm = 'SignupForm',
}

interface IState {
  loading: boolean;
  ocAuthReady: boolean;
  refreshPage: boolean;
  searchString: string;
  showModal: boolean;
  view: ModalView | undefined;
}

type ModalActionType =
  | 'SET_SEARCH_STRING'
  | 'SET_OC_AUTH_READY'
  | 'HIDE_MODAL'
  | 'HIDE_MODAL_AND_REFRESH_PAGE'
  | 'REFRESH_PAGE'
  | 'SET_VIEW'
  | 'SET_SHOW_MODAL_VIEW_AND_LOADING';

interface ModalAction {
  payload?: {
    loading: boolean;
    searchString: string;
    showModal: boolean;
    view: ModalView;
  };
  type: ModalActionType;
}

export type ModalProviderData = [IState, Dispatch<ModalAction>];

function modalReducer(state: IState, action: ModalAction): IState {
  switch (action.type) {
    case 'SET_SEARCH_STRING':
      return {
        ...state,
        searchString: action.payload?.searchString ?? '',
      };

    case 'SET_OC_AUTH_READY':
      return {
        ...state,
        ocAuthReady: true,
      };

    case 'HIDE_MODAL':
      return {
        ...state,
        showModal: false,
        view: undefined,
        searchString: '',
      };

    case 'HIDE_MODAL_AND_REFRESH_PAGE':
      return {
        ...state,
        showModal: false,
        view: undefined,
        refreshPage: true,
      };

    case 'REFRESH_PAGE':
      return {
        ...state,
        refreshPage: true,
      };

    case 'SET_VIEW':
      return {
        ...state,
        showModal: true,
        view: action.payload?.view,
        loading: false,
      };

    case 'SET_SHOW_MODAL_VIEW_AND_LOADING':
      return {
        ...state,
        showModal: !!action.payload?.showModal,
        view: action.payload?.view ?? state.view,
        loading: !!action.payload?.loading,
      };

    default:
      return state;
  }
}

const initialState: IState = {
  searchString: '',
  ocAuthReady: false,
  loading: true,
  showModal: false,
  view: undefined,
  refreshPage: false,
};

export const ModalContext = createContext<ModalProviderData>([initialState, () => null]);
ModalContext.displayName = 'ModalContext';

const ModalProvider = ({ children }: PropsWithChildren) => {
  const [
    {
      // prettier-ignore
      loading,
      ocAuthReady,
      refreshPage,
      searchString,
      showModal,
      view,
    },
    dispatch,
  ] = useReducer(modalReducer, initialState);

  const value: ModalProviderData = useMemo(
    () => [
      {
        // prettier-ignore
        searchString,
        ocAuthReady,
        loading,
        showModal,
        view,
        refreshPage,
      },
      dispatch,
    ],
    [
      // prettier-ignore
      searchString,
      ocAuthReady,
      loading,
      showModal,
      view,
      refreshPage,
      dispatch,
    ],
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

export const useModalContext = () => {
  const modalContext = useContext(ModalContext);
  if (!modalContext) {
    throw new Error('useModalContext can only be used inside of ModalProvider');
  }

  return modalContext;
};

export function withModalContext<T = Record<string, unknown>>(
  Component: ComponentType<T & unknown>,
): (props: T & unknown) => React.JSX.Element {
  const WrappedComponent = (props: T & unknown) => {
    const context = useModalContext();

    return <Component {...props} context={context} />;
  };

  return WrappedComponent;
}

export default ModalProvider;
